"use client";

/**
 * Top→bottom soft reveal for the whole investigation rail.
 * Callers: InsightsInvestigationSidebar. Local animation only — no API schema.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/shared/lib/utils/cn";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Top→bottom soft reveal for the whole investigation rail (Coverage + Tasks +
 * Working frame as one mask). Caller: InsightsInvestigationSidebar.
 * Plays once when `active` becomes true; later content updates stay visible.
 */
export function RailReveal({
  active,
  children,
  durationMs = 1100,
}: {
  /** True once the rail has something to show. */
  active: boolean;
  children: ReactNode;
  durationMs?: number;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const doneRef = useRef(false);

  // Reset play flag when the rail clears — adjust during render (not in effect).
  if (!active && playing) {
    setPlaying(false);
  }

  useEffect(() => {
    if (!active) {
      doneRef.current = false;
      const el = rootRef.current;
      if (el) {
        el.style.removeProperty("--rail-reveal");
        el.style.removeProperty("opacity");
      }
      return;
    }

    if (doneRef.current) return;

    if (prefersReducedMotion()) {
      doneRef.current = true;
      return;
    }

    const el = rootRef.current;
    if (!el) return;

    let cancelled = false;
    // Start after paint so setState is not synchronous in the effect body.
    const startId = window.requestAnimationFrame(() => {
      if (cancelled) return;
      setPlaying(true);
      el.style.setProperty("--rail-reveal", "0%");
      el.style.opacity = "0.35";

      const start = performance.now();
      let raf = 0;
      const tick = (now: number) => {
        if (cancelled) return;
        const t = Math.min(1, (now - start) / durationMs);
        const eased = 1 - (1 - t) ** 3;
        const pct = eased * 130;
        el.style.setProperty("--rail-reveal", `${pct}%`);
        el.style.opacity = String(0.35 + 0.65 * eased);
        if (t < 1) {
          raf = window.requestAnimationFrame(tick);
        } else {
          el.style.setProperty("--rail-reveal", "130%");
          el.style.opacity = "1";
          doneRef.current = true;
          setPlaying(false);
          el.style.removeProperty("--rail-reveal");
          el.style.removeProperty("opacity");
        }
      };
      raf = window.requestAnimationFrame(tick);

      // Stash raf cancel on the outer cleanup via mutable holder.
      cancelInner = () => window.cancelAnimationFrame(raf);
    });

    let cancelInner: (() => void) | undefined;

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(startId);
      cancelInner?.();
      // Strict Mode remount: clear partial mask so the next effect can replay.
      if (!doneRef.current) {
        el.style.removeProperty("--rail-reveal");
        el.style.removeProperty("opacity");
      }
    };
  }, [active, durationMs]);

  return (
    <div ref={rootRef} className={cn(active && playing && "rail-sweep-fade")}>
      {children}
    </div>
  );
}
